/**
 * TypeScript port of the Juniper firewall Jinja2 templates and globals.
 *
 * Mirrors:
 *   jinja_templates/globals/common.py           → IP utility functions
 *   jinja_templates/templates/firewall/juniper/  → renderFirewallConfig()
 *
 * Each render* function corresponds to a .j2 template section, producing
 * identical output to the Python renderer.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

/** Snake-case VLAN shape expected by the Jinja2 templates. */
export interface TemplateVlan {
  number: string;
  name: string;
  nickname: string;       // empty string when null
  ip: string;             // resolved gateway/interface IP
  cidr: string;
  network_address: string; // resolved network base address
  dhcp: boolean;
  dhcp_low: string;
  dhcp_high: string;
  dhcp_router: string;
}

export interface FirewallConfigVars {
  MARSHA: string;
  HOTEL_SUBNET: string;
  PUBLIC_IP: string;
  PUBLIC_NETWORK_MASK: string;
  GATEWAY: string;
  SYSTEM_TIME_ZONE: string;
  NETWORK_MASK: string;   // "21" | "22" etc.
  type: string;           // "Basic" | "MIC" | "Full"
  ROUTABLE_VLANS: TemplateVlan[];
  VENDOR_VLANS: TemplateVlan[];
  MULTI_CIRCUIT: boolean;
  CLEAR_ALERTS: boolean;
}

// ─── Global helpers (mirrors globals/common.py) ────────────────────────────

function solveIpEquation(equation: string, baseIp: string): string {
  if (!baseIp || !baseIp.includes(".")) return equation;
  const octets = baseIp.split(".");
  const [w, x, y, z] = octets.map(Number);
  const vars: Record<string, number> = { w, x, y, z };
  return equation
    .split(".")
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      const m = part.match(/^([wxyz])([+-]\d+)?$/);
      if (!m) return part;
      const base = vars[m[1]] ?? 0;
      const delta = m[2] ? parseInt(m[2], 10) : 0;
      return String(Math.min(255, Math.max(0, base + delta)));
    })
    .join(".");
}

/** Converts (ip, dotted-mask) → CIDR network string. Mirrors get_public_subnet(). */
function getPublicSubnet(ip: string, mask: string): string {
  if (!ip || !mask) return `${ip}/0`;
  const ipOcts = ip.split(".").map(Number);
  const mOcts = mask.split(".").map(Number);
  const net = ipOcts.map((o, i) => o & (mOcts[i] ?? 0));
  const bits = mOcts.reduce(
    (acc, o) => acc + (o >>> 0).toString(2).split("").filter((b) => b === "1").length,
    0,
  );
  return `${net.join(".")}/${bits}`;
}

// ─── Template sections ────────────────────────────────────────────────────────

/** sections/initial.j2 */
function renderInitial(v: FirewallConfigVars): string {
  const LOG  = solveIpEquation("w.x.y+3.254", v.HOTEL_SUBNET);
  const FILT = solveIpEquation("w.x.y+3.z+129", v.HOTEL_SUBNET);
  const FEND = solveIpEquation("w.x.y+3.254", v.HOTEL_SUBNET);

  return `# ================= CLEAN UP =================

delete interfaces
delete security zones
delete security nat
delete security policies
delete access
delete vlans
delete chassis auto-image-upgrade
delete forwarding-options helpers bootp

# =============== MISCELLANEOUS ==============

set system host-name ${v.MARSHA}FW01-01-MDF
set system services ssh
set system accounting events login
set system accounting events change-log
set system login announcement "\\n\\tUNAUTHORIZED ACCESS TO THIS DEVICE IS PROHIBITED\\n\\n\\tThis system, which includes the data stored\\n\\therein, is proprietary and confidential to\\n\\tMarriott International, Inc. (Marriott)\\n\\n\\tThis system is for Marriott authorized\\n\\tpersonnel only. Unauthorized access is\\n\\tprohibited and will be prosecuted to the\\n\\tfullest extent of the applicable law.\\n\\n"
set system login message "\\n\\tUNAUTHORIZED ACCESS TO THIS DEVICE IS PROHIBITED\\n\\tThis system, which includes the data stored\\n\\ttherein, is proprietary and confidential to\\n\\tMarriott International, Inc. (Marriott)\\n\\n"
set system services netconf ssh
set system services web-management https system-generated-certificate
set system time-zone ${v.SYSTEM_TIME_ZONE}

# =================== LOGS ===================

set system syslog archive size 100k
set system syslog archive files 3
set system syslog user * any emergency
set system syslog host 162.130.146.34 any warning
set system syslog host 162.130.146.34 change-log warning
set system syslog host 162.130.146.34 interactive-commands warning
set system syslog host 162.130.146.34 match "(UI_COMMIT:)|(UI_COMMIT_AT_COMPLETED)|(RT_FLOW_SESSION_CREATE)|(RT_FLOW_S ESSION_DENY)|(RT_FLOW_SESSION_CLOSE)"
set system syslog file interactive-commands interactive-commands any
set system syslog file messages any notice
set system syslog file messages authorization info
set security log mode stream
set security log source-address ${LOG}
set security log stream Firemon format sd-syslog
set security log stream Firemon host 162.130.146.34

# ============= GENERAL SECURITY =============

set security screen ids-option untrust-screen icmp ping-death
set security screen ids-option untrust-screen ip source-route-option
set security screen ids-option untrust-screen ip tear-drop
set security screen ids-option untrust-screen tcp syn-flood alarm-threshold 1024
set security screen ids-option untrust-screen tcp syn-flood attack-threshold 200
set security screen ids-option untrust-screen tcp syn-flood source-threshold 1024
set security screen ids-option untrust-screen tcp syn-flood destination-threshold 2048
set security screen ids-option untrust-screen tcp syn-flood timeout 20
set security screen ids-option untrust-screen tcp land

# ================ ADMIN USER ================

set system login user admin uid 2002 class super-user authentication plain-text-password
1ns2deout
1ns2deout

set system root-authentication plain-text-password
1ns2deout
1ns2deout

# ================== RADIUS ==================

set system radius-server 192.168.223.130 port 1812
set system radius-server 192.168.223.130 accounting-port 1813
set system radius-server 192.168.223.130 secret "JAr5frAr"
set system radius-server 192.168.223.130 source-address 192.168.223.129
set system radius-options attributes nas-ip-address 192.168.223.129
set system accounting events login
set system accounting events change-log
set system accounting events interactive-commands
set system accounting destination radius server 192.168.223.130 accounting-port 1813
set system accounting destination radius server 192.168.223.130 secret "JAr5frAr"
set system accounting destination radius server 192.168.223.130 source-address 192.168.223.129
set system authentication-order radius

# ============================================
# REMOTE ACCESS FROM GTK
# ============================================

# ================= GUEST TEK ================
set policy-options prefix-list GTKIPRanges 41.33.105.160/27
set policy-options prefix-list GTKIPRanges 46.245.240.0/24
set policy-options prefix-list GTKIPRanges 46.245.244.0/24
set policy-options prefix-list GTKIPRanges 62.50.192.0/21
set policy-options prefix-list GTKIPRanges 62.50.192.128/25
set policy-options prefix-list GTKIPRanges 62.50.207.0/24
set policy-options prefix-list GTKIPRanges 62.111.142.128/27
set policy-options prefix-list GTKIPRanges 63.236.111.164/32
set policy-options prefix-list GTKIPRanges 94.200.145.130/32
set policy-options prefix-list GTKIPRanges 103.221.68.0/29
set policy-options prefix-list GTKIPRanges 118.185.48.160/29
set policy-options prefix-list GTKIPRanges 122.15.114.64/27
set policy-options prefix-list GTKIPRanges 123.63.70.252/30
set policy-options prefix-list GTKIPRanges 176.35.242.96/27
set policy-options prefix-list GTKIPRanges 204.246.141.0/24
set policy-options prefix-list GTKIPRanges 208.98.198.96/27
set policy-options prefix-list GTKIPRanges 213.216.68.10/32
set policy-options prefix-list GTKIPRanges 216.123.82.128/26
set policy-options prefix-list GTKIPRanges 216.123.82.128/27
set policy-options prefix-list GTKIPRanges 216.123.84.0/27
set policy-options prefix-list GTKIPRanges 216.123.84.32/27

# ================== FIREMON =================

set policy-options prefix-list FiremonIPRanges 12.229.8.253/32
set policy-options prefix-list FiremonIPRanges 12.229.8.254/32
set policy-options prefix-list FiremonIPRanges 162.130.1.253/32
set policy-options prefix-list FiremonIPRanges 162.130.1.254/32

# ============== FIREWALL FAMILY =============

set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from source-address ${FILT}/32 except
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from destination-address ${FEND}/32
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from protocol tcp
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH from destination-port ssh
set firewall family inet filter GTK_MGMT_Filter term Allow_SSH then discard
set firewall family inet filter GTK_MGMT_Filter term Allow_All then accept

set firewall family inet filter MI-PBR term 1 from source-address ${v.HOTEL_SUBNET}/22
set firewall family inet filter MI-PBR term 1 then routing-instance MI-Connector
set firewall family inet filter MI-PBR term 2 then accept

# ============== FIREWALL FILTER =============

set firewall filter REMOTE_MGMT_FILTER term allow_management from source-address 0.0.0.0/0
set firewall filter REMOTE_MGMT_FILTER term allow_management from destination-address ${v.PUBLIC_IP}/32
set firewall filter REMOTE_MGMT_FILTER term allow_management from source-prefix-list GTKIPRanges except
set firewall filter REMOTE_MGMT_FILTER term allow_management from source-prefix-list FiremonIPRanges except
set firewall filter REMOTE_MGMT_FILTER term allow_management from protocol tcp
set firewall filter REMOTE_MGMT_FILTER term allow_management from destination-port ssh
set firewall filter REMOTE_MGMT_FILTER term allow_management from destination-port http
set firewall filter REMOTE_MGMT_FILTER term allow_management from destination-port https
set firewall filter REMOTE_MGMT_FILTER term allow_management then count mgmt-filter-allow_mgmt
set firewall filter REMOTE_MGMT_FILTER term allow_management then discard
set firewall filter REMOTE_MGMT_FILTER term allow_everything_else then accept


# = CONFIGURE APPLICATIONS OVI AND SWITCHES =
set applications application OVI-SSH protocol tcp
set applications application OVI-SSH destination-port 443
set applications application SW-SSH protocol tcp
set applications application SW-SSH destination-port 22
set applications application SW-HTTPS protocol tcp
set applications application SW-HTTPS destination-port 443
set applications application SW-SNMP protocol tcp
set applications application SW-SNMP destination-port 161
set applications application SW-LOG protocol tcp
set applications application SW-LOG destination-port 514`;
}

/** sections/routable_sections/interfaces.j2 */
function renderInterfaces(v: FirewallConfigVars): string {
  const GPNS_CONNECTOR = solveIpEquation("w.x.y+3.98", v.HOTEL_SUBNET) + "/29";
  const mgmtVlans = v.ROUTABLE_VLANS.filter(
    (vl) => vl.number === "100" || vl.number === "130",
  );
  const gpnsVlans = v.ROUTABLE_VLANS.filter(
    (vl) => vl.number !== "100" && vl.number !== "130",
  );

  const mgmtInterfaces = mgmtVlans
    .map((vl) =>
      `set interfaces ge-0/0/1 unit ${vl.number} description ${vl.number === "100" ? "Switch_Management" : "OVI_Management"}
set interfaces ge-0/0/1 unit ${vl.number} vlan-id ${vl.number}
set interfaces ge-0/0/1 unit ${vl.number} family inet address ${vl.ip}/${vl.cidr}`,
    )
    .join("\n");

  const mgmtZones = mgmtVlans
    .map((vl) => `set security zones security-zone GTK_MGMT interfaces ge-0/0/1.${vl.number}`)
    .join("\n");

  const oviVlan = v.ROUTABLE_VLANS.find((vl) => vl.number === "130");
  const swVlan  = v.ROUTABLE_VLANS.find((vl) => vl.number === "100");

  const oviAddressBook = oviVlan
    ? `set security address-book GTK_Mgmt_Book address Obj_OVI ${solveIpEquation("w.x.y.z+2", oviVlan.network_address)}/32
set security address-book global address Obj_OVI_Management_GL ${solveIpEquation("w.x.y.z+2", oviVlan.network_address)}/32`
    : "";

  const swAddressBook = swVlan
    ? `set security address-book GTK_Mgmt_Book address Obj_Switches_100 ${swVlan.network_address}/${swVlan.cidr}
set security address-book global address Obj_Switches_100_GL ${swVlan.network_address}/${swVlan.cidr}`
    : "";

  const gpnsInterfaces =
    v.type === "MIC" || v.type === "Full"
      ? gpnsVlans
          .map(
            (vl) =>
              `set interfaces ge-0/0/2 unit ${vl.number} description IF_${vl.nickname || vl.name.replace(/ /g, "_")}
set interfaces ge-0/0/2 unit ${vl.number} vlan-id ${vl.number}
set interfaces ge-0/0/2 unit ${vl.number} family inet address ${vl.ip}/${vl.cidr}
set interfaces ge-0/0/2 unit ${vl.number} family inet filter input MI-PBR`,
          )
          .join("\n")
      : "";

  const gpnsConnectorLine =
    v.type === "Basic"
      ? `set interfaces ge-0/0/3 unit 905 description IF_GPNS_Basic_Connector
set interfaces ge-0/0/3 unit 905 vlan-id 905
set interfaces ge-0/0/3 unit 905 family inet address ${GPNS_CONNECTOR}`
      : `set interfaces ge-0/0/3 unit 900 description IF_GPNS_${v.type === "MIC" ? "MIC" : "Full"}_Connector
set interfaces ge-0/0/3 unit 900 vlan-id 900
set interfaces ge-0/0/3 unit 900 family inet address ${GPNS_CONNECTOR}`;

  return `# ============================================
# MANAGEMENT
# ============================================

set system name-server 8.8.8.8

# ============== INTERFACE & DNS =============

set interfaces ge-0/0/1 vlan-tagging
${mgmtInterfaces}
# ================== ZONES =================

${mgmtZones}
set security zones security-zone GTK_MGMT host-inbound-traffic system-services all

# ============== ADDRESS BOOK ==============

set security address-book GTK_Mgmt_Book attach zone GTK_MGMT
${oviAddressBook}
${swAddressBook}

# ============================================
# INTERFACES
# ============================================

# ============== INTERNET INTERFACES ==============

set interfaces ge-0/0/0 unit 0 description IF_INTERNET
set interfaces ge-0/0/0 unit 0 family inet address ${v.PUBLIC_IP}/29
set interfaces ge-0/0/0 unit 0 family inet filter input REMOTE_MGMT_FILTER

# ============== GPNS INTERFACES =============

set interfaces ge-0/0/2 description INTERNAL
set interfaces ge-0/0/2 vlan-tagging
${gpnsInterfaces}

# ================== GENERIC =================

set interfaces ge-0/0/3 description Marriott_WAN
set interfaces ge-0/0/3 vlan-tagging
${gpnsConnectorLine}`;
}

/** sections/routable_sections/zones.j2 */
function renderZones(v: FirewallConfigVars): string {
  const internalVlans = v.ROUTABLE_VLANS.filter((vl) => vl.number !== "100");
  const internalInterfaces =
    v.type === "MIC" || v.type === "Full"
      ? internalVlans
          .map((vl) => `set security zones security-zone INTERNAL interfaces ge-0/0/2.${vl.number}`)
          .join("\n")
      : "";

  return `# ================= INTERNET =================

set security zones security-zone INTERNET host-inbound-traffic system-services ping
set security zones security-zone INTERNET host-inbound-traffic system-services ssh
set security zones security-zone INTERNET interfaces ge-0/0/0.0

# =============== MARRIOTT_WAN ===============

${v.type === "Basic"
    ? "set security zones security-zone MARRIOTT_WAN interfaces ge-0/0/3.905"
    : "set security zones security-zone MARRIOTT_WAN interfaces ge-0/0/3.900"}
set security zones security-zone MARRIOTT_WAN host-inbound-traffic system-services all

# =============== INTERNAL ===============

set security zones security-zone INTERNAL host-inbound-traffic system-services all
${internalInterfaces}`;
}

/** sections/routable_sections/dhcp.j2 */
function renderDhcp(v: FirewallConfigVars): string {
  const pools = v.ROUTABLE_VLANS
    .flatMap((vl) =>
      vl.dhcp
        ? [
            `set system services dhcp pool ${vl.network_address}/${vl.cidr} address-range low ${vl.dhcp_low}
set system services dhcp pool ${vl.network_address}/${vl.cidr} address-range high ${vl.dhcp_high}
set system services dhcp pool ${vl.network_address}/${vl.cidr} default-lease-time 3600
set system services dhcp pool ${vl.network_address}/${vl.cidr} router ${vl.dhcp_router}
`,
          ]
        : [],
    )
    .join("\n");
  return pools;
}

/** sections/routable_sections/address_book.j2 */
function renderAddressBook(v: FirewallConfigVars): string {
  const GPNS_CONNECTOR     = solveIpEquation("w.x.y+3.96", v.HOTEL_SUBNET) + "/29";
  const BASIC_PROP_SUBNET  = solveIpEquation("w.x.y+3.z", v.HOTEL_SUBNET) + "/24";
  const MIC_PROP_SUBNET    = solveIpEquation("w.x.y+2.z", v.HOTEL_SUBNET) + "/23";
  const MARSHA_ADDRESS     = solveIpEquation("w.x.y.2", v.HOTEL_SUBNET) + "/32";

  const globalAddresses =
    v.type !== "Basic"
      ? v.ROUTABLE_VLANS.map(
          (vl) =>
            `set security address-book global address ${vl.nickname || vl.name.replace(/ /g, "_")}_GL description UsedbyNAT
set security address-book global address ${vl.nickname || vl.name.replace(/ /g, "_")}_GL ${vl.network_address}/${vl.cidr}`,
        ).join("\n")
      : "";

  let internalBook = "";
  if (v.type === "Basic") {
    internalBook = `set security address-book Internal_Address_Book address Obj_Property_Subnet ${BASIC_PROP_SUBNET}`;
  } else if (v.type === "MIC") {
    const vlanAddrs = v.ROUTABLE_VLANS.map(
      (vl) =>
        `set security address-book Internal_Address_Book address Obj_${vl.nickname || vl.name.replace(/ /g, "_")} ${vl.network_address}/${vl.cidr}`,
    ).join("\n");
    internalBook = `${vlanAddrs}
set security address-book Internal_Address_Book address Obj_Property_Subnet ${MIC_PROP_SUBNET}`;
  } else if (v.type === "Full") {
    const vlanAddrs = v.ROUTABLE_VLANS.map(
      (vl) =>
        `set security address-book Internal_Address_Book address Obj_${vl.nickname || vl.name.replace(/ /g, "_")} ${vl.network_address}/${vl.cidr}`,
    ).join("\n");
    internalBook = `${vlanAddrs}
set security address-book Internal_Address_Book address Obj_Property_Subnet ${v.HOTEL_SUBNET}/${v.NETWORK_MASK}
set security address-book Internal_Address_Book address Obj_${v.MARSHA}IFC ${MARSHA_ADDRESS}`;
  }

  const gpnsConnectorLabel =
    v.type === "Basic"
      ? `set security address-book MARRIOTT_WAN_Book address Obj_GPNS_Basic_Connector ${GPNS_CONNECTOR}`
      : v.type === "MIC"
        ? `set security address-book MARRIOTT_WAN_Book address Obj_GPNS_MIC_Connector ${GPNS_CONNECTOR}`
        : `set security address-book MARRIOTT_WAN_Book address Obj_GPNS_Full_Connector ${GPNS_CONNECTOR}`;

  return `# ================= GLOBAL ADDRESSES =================

${globalAddresses}

# ================= INTERNET =================

set security address-book INTERNET_Book address Obj_INTERNET ${getPublicSubnet(v.PUBLIC_IP, v.PUBLIC_NETWORK_MASK)}
set security address-book INTERNET_Book attach zone INTERNET

# =============== INTERNAL ===============

${internalBook}
set security address-book Internal_Address_Book attach zone INTERNAL

# =============== MARRIOTT_WAN ===============

${gpnsConnectorLabel}
set security address-book MARRIOTT_WAN_Book address Obj_ACS_Server 162.130.146.47/32
set security address-book MARRIOTT_WAN_Book address Obj_BackOffice ${v.HOTEL_SUBNET}/23
set security address-book MARRIOTT_WAN_Book address-set ObjGrp_ACS_Servers address Obj_ACS_Server
set security address-book MARRIOTT_WAN_Book attach zone MARRIOTT_WAN


# ================== VENDORS =================

set security address-book VENDORS_Book attach zone VENDORS`;
}

/** sections/routable_sections/nat.j2 */
function renderNat(v: FirewallConfigVars): string {
  const internalNat =
    v.type !== "Basic"
      ? `set security nat source rule-set INTERNAL-to-INTERNET-rs from zone INTERNAL
set security nat source rule-set INTERNAL-to-INTERNET-rs to zone INTERNET
${v.ROUTABLE_VLANS.map(
  (vl) =>
    `set security nat source rule-set INTERNAL-to-INTERNET-rs rule ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET-r1 match source-address-name ${vl.nickname || vl.name.replace(/ /g, "_")}_GL
set security nat source rule-set INTERNAL-to-INTERNET-rs rule ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET-r1 match destination-address 0.0.0.0/0
set security nat source rule-set INTERNAL-to-INTERNET-rs rule ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET-r1 then source-nat interface`,
).join("\n")}`
      : `set security nat source rule-set NAT-to-IF_INTERNET from zone INTERNAL
set security nat source rule-set NAT-to-IF_INTERNET to zone INTERNET
set security nat source rule-set NAT-to-IF_INTERNET rule NAT_to_Internet match source-address 0.0.0.0/0
set security nat source rule-set NAT-to-IF_INTERNET rule NAT_to_Internet match destination-address 0.0.0.0/0
set security nat source rule-set NAT-to-IF_INTERNET rule NAT_to_Internet then source-nat interface`;

  return `${internalNat}

set security nat source pool SW-NAT address 10.147.251.89/32
set security nat source pool OVI-NAT address 10.147.251.90/32

set security nat source rule-set OVI-SW-NAT from zone GTK_MGMT
set security nat source rule-set OVI-SW-NAT to zone GTK_MGMT
set security nat source rule-set OVI-SW-NAT rule OVI_to_SW match source-address-name OVI_Management_GL
set security nat source rule-set OVI-SW-NAT rule OVI_to_SW match destination-address-name Obj_Switches-100_GL
set security nat source rule-set OVI-SW-NAT rule OVI_to_SW then source-nat pool OVI-NAT
set security nat source rule-set OVI-SW-NAT rule SW_to_OVI match source-address-name Obj_Switches-100_GL
set security nat source rule-set OVI-SW-NAT rule SW_to_OVI match destination-address-name OVI_Management_GL
set security nat source rule-set OVI-SW-NAT rule SW_to_OVI then source-nat pool SW-NAT`;
}

/** sections/routable_sections/security_policies.j2 */
function renderSecurityPolicies(v: FirewallConfigVars): string {
  if (v.type === "Basic") return "";

  const toInternet = v.ROUTABLE_VLANS.map(
    (vl) =>
      `set security policies from-zone INTERNAL to-zone INTERNET policy ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET match source-address Obj_${vl.nickname || vl.name.replace(/ /g, "_")}
set security policies from-zone INTERNAL to-zone INTERNET policy ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET match destination-address any
set security policies from-zone INTERNAL to-zone INTERNET policy ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET match application any
set security policies from-zone INTERNAL to-zone INTERNET policy ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET then permit
set security policies from-zone INTERNAL to-zone INTERNET policy ${vl.nickname || vl.name.replace(/ /g, "_")}-INTERNET then count
`,
  ).join("\n");

  const toMarriott = v.ROUTABLE_VLANS.map(
    (vl) =>
      `set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy INTERNAL_TO_MARRIOTT_WAN match source-address Obj_${vl.nickname || vl.name.replace(/ /g, "_")}
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy INTERNAL_TO_MARRIOTT_WAN match destination-address any
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy INTERNAL_TO_MARRIOTT_WAN match application any
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy INTERNAL_TO_MARRIOTT_WAN then permit
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy INTERNAL_TO_MARRIOTT_WAN then count
`,
  ).join("\n");

  const marriottToInternal = v.ROUTABLE_VLANS
    .flatMap((vl) =>
      vl.number !== "100"
        ? [
            `set security policies from-zone MARRIOTT_WAN to-zone INTERNAL policy VLAN_${vl.number} match source-address Obj_BackOffice
set security policies from-zone MARRIOTT_WAN to-zone INTERNAL policy VLAN_${vl.number} match destination-address Obj_${vl.nickname || vl.name.replace(/ /g, "_")}
set security policies from-zone MARRIOTT_WAN to-zone INTERNAL policy VLAN_${vl.number} match application any
set security policies from-zone MARRIOTT_WAN to-zone INTERNAL policy VLAN_${vl.number} then permit
set security policies from-zone MARRIOTT_WAN to-zone INTERNAL policy VLAN_${vl.number} then count
`,
          ]
        : [],
    )
    .join("\n");

  const gtkToInternal =
    v.type === "Full"
      ? `# ============== GTK MANAGEMENT TO INTERNAL ==============

set security policies from-zone GTK_MGMT to-zone INTERNAL policy VLAN_100 match source-address Obj_GTK_MGMT
set security policies from-zone GTK_MGMT to-zone INTERNAL policy VLAN_100 match destination-address Obj_${v.MARSHA}IFC
set security policies from-zone GTK_MGMT to-zone INTERNAL policy VLAN_100 match application any
set security policies from-zone GTK_MGMT to-zone INTERNAL policy VLAN_100 then permit
set security policies from-zone GTK_MGMT to-zone INTERNAL policy VLAN_100 then count
`
      : "";

  return `# =============== INTERNAL TO INTERNET ===============

${toInternet}
# =============== INTERNAL TO MARRIOTT_WAN ===============

${toMarriott}
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy Property_to_Marriott match source-address Obj_Property_Subnet
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy Property_to_Marriott match destination-address any
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy Property_to_Marriott match application any
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy Property_to_Marriott then permit
set security policies from-zone INTERNAL to-zone MARRIOTT_WAN policy Property_to_Marriott then count

# =============== MARRIOTT_WAN TO INTERNAL ===============

${marriottToInternal}
# ============== SW TO OVI =============

set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match source-address Obj_Switches-100
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match destination-address Obj_OVI
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application junos-icmp-all
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application junos-radius
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application junos-radacct
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application junos-ntp
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application junos-tftp
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application SW-SNMP
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI match application SW-LOG
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy SW-to-OVI then permit

# ============== OVI TO SW =============

set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match source-address Obj_OVI
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match destination-address Obj_Switches-100
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match application junos-icmp-all
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match application SW-SSH
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match application SW-HTTPS
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW match application SW-SNMP
set security policies from-zone GTK_MGMT to-zone GTK_MGMT policy OVI-to-SW then permit

# ============== GTK MANAGEMENT TO MARRIOTT_WAN ==============

set security policies from-zone GTK_MGMT to-zone MARRIOTT_WAN policy VLAN_100 match source-address Obj_GTK_MGMT
set security policies from-zone GTK_MGMT to-zone MARRIOTT_WAN policy VLAN_100 match destination-address ObjGrp_ACS_Servers
set security policies from-zone GTK_MGMT to-zone MARRIOTT_WAN policy VLAN_100 match application any
set security policies from-zone GTK_MGMT to-zone MARRIOTT_WAN policy VLAN_100 then permit
set security policies from-zone GTK_MGMT to-zone MARRIOTT_WAN policy VLAN_100 then count

${gtkToInternal}`;
}

/** SNMP + protocols section (inline in main.j2) */
function renderSnmpAndProtocols(v: FirewallConfigVars): string {
  const MGMT_ADDR = solveIpEquation("w.x.y+3.254", v.HOTEL_SUBNET);
  return `# ============================================
# SNMP
# ============================================

# ================= GUEST-TEK ================

set snmp location "${v.MARSHA} At MDF"
set snmp v3 usm local-engine user gtkadmin authentication-md5 authentication-password dijipAD10efr
set snmp v3 usm local-engine user gtkadmin privacy-des privacy-password Gu35tt3k4dm1n
set snmp v3 vacm security-to-group security-model usm security-name gtkadmin group gtk
set snmp v3 vacm access group gtk default-context-prefix security-model usm security-level privacy read-view SNMPVIEW
set snmp v3 vacm access group gtk default-context-prefix security-model usm security-level privacy write-view SNMPVIEW
set snmp view SNMPVIEW oid .1 include

# ================== MARRIOTT =================

set snmp client-list Marriott_WAN 10.212.146.124/32
set snmp client-list Marriott_WAN 10.212.146.36/32
set snmp client-list Marriott_WAN 10.212.146.57/32
set snmp client-list Marriott_WAN 10.212.146.75/32
set snmp client-list Marriott_WAN 10.212.146.54/32
set snmp client-list Marriott_WAN 10.212.146.39/32
set snmp client-list Marriott_WAN 10.212.146.58/32
set snmp community switchback authorization read-only
set snmp community mar3csn authorization read-only
set snmp community mar3csn client-list-name Marriott_WAN

# ============================================
# PROTOCOLS
# ============================================

set protocols lldp management-address ${MGMT_ADDR}
set protocols lldp interface ge-0/0/1
set protocols rstp disable`;
}

/** sections/routable_sections/routes.j2 */
function renderRoutes(v: FirewallConfigVars): string {
  const LOCAL = solveIpEquation("w.x.y+3.97", v.HOTEL_SUBNET);
  return `# ============================================
# ROUTES
# ============================================

set routing-options static route 10.0.0.0/8 next-hop ${LOCAL}
set routing-options static route 159.166.0.0/16 next-hop ${LOCAL}
set routing-options static route 162.130.0.0/16 next-hop ${LOCAL}
set routing-options static route 162.130.0.0/23 next-hop ${v.GATEWAY}
set routing-options static route 162.130.196.0/22 next-hop ${v.GATEWAY}
set routing-options static route 172.25.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.26.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.27.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.28.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.29.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.30.0.0/16 next-hop ${LOCAL}
set routing-options static route 172.31.64.0/19 next-hop ${LOCAL}
set routing-options static route 172.31.160.0/19 next-hop ${LOCAL}
set routing-options static route 192.168.72.0/24 next-hop ${LOCAL}
set routing-options static route 192.168.73.0/24 next-hop ${LOCAL}
set routing-options static route 192.168.74.0/24 next-hop ${LOCAL}
set routing-options static route 141.143.0.0/16 next-hop ${LOCAL}
set routing-options static route 141.146.0.0/16 next-hop ${LOCAL}
set routing-options static route 0.0.0.0/0 next-hop ${v.GATEWAY}

set routing-instances MI-Connector instance-type forwarding
set routing-instances MI-Connector routing-options static route 0.0.0.0/0 next-hop ${LOCAL}/29
set routing-options interface-routes rib-group inet Import-Physical
set routing-options static route 0.0.0.0/0 next-hop ${v.GATEWAY}
set routing-options rib-groups Import-Physical import-rib inet.0
set routing-options rib-groups Import-Physical import-rib MI-Connector.inet.0`;
}

/** sections/vendors.j2 */
function renderVendors(v: FirewallConfigVars): string {
  if (v.VENDOR_VLANS.length === 0) return "";
  const PMS = solveIpEquation("w.x.y.z+2", v.HOTEL_SUBNET);

  const label = (vl: TemplateVlan) =>
    vl.nickname || vl.name.replace(/ /g, "_");

  const interfaces = v.VENDOR_VLANS.map(
    (vl) =>
      `set interfaces ge-0/0/2 unit ${vl.number} description IF_${label(vl)}
set interfaces ge-0/0/2 unit ${vl.number} vlan-id ${vl.number}
set interfaces ge-0/0/2 unit ${vl.number} family inet address ${vl.ip}`,
  ).join("\n");

  const zones = v.VENDOR_VLANS.map(
    (vl) => `set security zones security-zone VENDORS interfaces ge-0/0/2.${vl.number}`,
  ).join("\n");

  const dhcp = v.VENDOR_VLANS
    .flatMap((vl) =>
      vl.dhcp
        ? [
            `set system services dhcp pool ${vl.network_address}/${vl.cidr} address-range low ${vl.dhcp_low}
set system services dhcp pool ${vl.network_address}/${vl.cidr} address-range high ${vl.dhcp_high}
set system services dhcp pool ${vl.network_address}/${vl.cidr} default-lease-time 3600
set system services dhcp pool ${vl.network_address}/${vl.cidr} router ${vl.dhcp_router}`,
          ]
        : [],
    )
    .join("\n");

  const globalAddrs = v.VENDOR_VLANS.map(
    (vl) =>
      `set security address-book global address ${label(vl)}_GL description UsedbyNAT
set security address-book global address ${label(vl)}_GL ${vl.network_address}/${vl.cidr}
`,
  ).join("\n");

  const pmsLine = v.VENDOR_VLANS.some((vl) => vl.number === "841")
    ? `set security address-book MARRIOTT_WAN_Book address PMS ${PMS}/32`
    : "";

  const vendorsBook = v.VENDOR_VLANS.map(
    (vl) =>
      `set security address-book Vendors_Address_Book address Obj_${label(vl)} ${vl.network_address}/${vl.cidr}`,
  ).join("\n");

  const nat = v.VENDOR_VLANS.map(
    (vl) =>
      `set security nat source rule-set NAT-to-IF_INTERNET rule ${label(vl)}-INTERNET-r1 match source-address-name ${label(vl)}_GL
set security nat source rule-set NAT-to-IF_INTERNET rule ${label(vl)}-INTERNET-r1 match destination-address 0.0.0.0/0
set security nat source rule-set NAT-to-IF_INTERNET rule ${label(vl)}-INTERNET-r1 then source-nat interface
`,
  ).join("\n");

  const vendorToMarriott = v.VENDOR_VLANS.map((vl) => {
    if (vl.number === "841") {
      return `set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS match source-address Obj_${label(vl)}
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS match destination-address Obj_BackOffice
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS match destination-address PMS
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS match application any
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS then permit
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy Mobile_Key-to-PMS then count
`;
    }
    return `set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy VLAN_${vl.number} match source-address Obj_${label(vl)}
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy VLAN_${vl.number} match destination-address Obj_BackOffice
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy VLAN_${vl.number} match application any
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy VLAN_${vl.number} then permit
set security policies from-zone VENDORS to-zone MARRIOTT_WAN policy VLAN_${vl.number} then count
`;
  }).join("\n");

  const marriottToVendor = v.VENDOR_VLANS.map((vl) => {
    if (vl.number === "841") {
      return `set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key match source-address PMS
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key match source-address Obj_BackOffice
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key match destination-address Obj_${label(vl)}
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key match application any
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key then permit
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy PMS-to-Mobile_Key then count
`;
    }
    return `set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy VLAN_${vl.number} match source-address Obj_BackOffice
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy VLAN_${vl.number} match destination-address Obj_${label(vl)}
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy VLAN_${vl.number} match application any
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy VLAN_${vl.number} then permit
set security policies from-zone MARRIOTT_WAN to-zone VENDORS policy VLAN_${vl.number} then count
`;
  }).join("\n");

  const vendorToInternet = v.VENDOR_VLANS.map(
    (vl) =>
      `set security policies from-zone VENDORS to-zone INTERNET policy ${label(vl)}-internet match source-address Obj_${label(vl)}
set security policies from-zone VENDORS to-zone INTERNET policy ${label(vl)}-internet match destination-address any
set security policies from-zone VENDORS to-zone INTERNET policy ${label(vl)}-internet match application any
set security policies from-zone VENDORS to-zone INTERNET policy ${label(vl)}-internet then permit
set security policies from-zone VENDORS to-zone INTERNET policy ${label(vl)}-internet then count`,
  ).join("\n");

  return `# ================ INTERFACES ================

${interfaces}

# =================== ZONES ==================

set security zones security-zone VENDORS host-inbound-traffic system-services all
${zones}

# =============== DHCP COMMANDS ==============

${dhcp}

# =============== ADDRESS BOOK ===============

${globalAddrs}
${pmsLine}
${vendorsBook}

# ==================== NAT ===================

${nat}
set security nat source rule-set NAT-to-IF_INTERNET from zone VENDORS
set security nat source rule-set NAT-to-IF_INTERNET to zone INTERNET

# ============= SECURITY POLICIES ============

# ========== VENDOR TO MARRIOTT_WAN ==========

${vendorToMarriott}
# ========== MARRIOTT_WAN TO VENDOR ==========

${marriottToVendor}
# =============== VENDOR TO INTERNET ==============

${vendorToInternet}`;
}

/** sections/additional_options.j2 */
function renderAdditionalOptions(v: FirewallConfigVars): string {
  const parts: string[] = [];
  if (v.MULTI_CIRCUIT) {
    parts.push(`# =============== MULTI_CIRCUIT ==============

set services rpm probe Probe-Server test Test_ISP target address 8.8.8.8
set services rpm probe Probe-Server test Test_ISP probe-count 10
set services rpm probe Probe-Server test Test_ISP probe-interval 5
set services rpm probe Probe-Server test Test_ISP test-interval 10
set services rpm probe Probe-Server test Test_ISP thresholds successive-loss 10
set services rpm probe Probe-Server test Test_ISP thresholds total-loss 5
set services rpm probe Probe-Server test Test_ISP destination-interface ge-0/0/0.915
set services rpm probe Probe-Server test Test_ISP next-hop 187.188.12.49

set services rpm probe Probe-Server1 test Test_ISP target address 8.8.8.8
set services rpm probe Probe-Server1 test Test_ISP probe-count 10
set services rpm probe Probe-Server1 test Test_ISP probe-interval 5
set services rpm probe Probe-Server1 test Test_ISP test-interval 10
set services rpm probe Probe-Server1 test Test_ISP thresholds successive-loss 10
set services rpm probe Probe-Server1 test Test_ISP thresholds total-loss 5
set services rpm probe Probe-Server1 test Test_ISP destination-interface ge-0/0/0.917
set services rpm probe Probe-Server1 test Test_ISP next-hop 192.168.217.1

set services ip-monitoring policy Server-Tracking match rpm-probe Probe-Server
set services ip-monitoring policy Server-Tracking then preferred-route routing-instances FBF-1 route 0.0.0.0/0 next-hop 187.188.12.49
set services ip-monitoring policy Server-Tracking1 match rpm-probe Probe-Server1
set services ip-monitoring policy Server-Tracking1 then preferred-route routing-instances FBF-2 route 0.0.0.0/0 next-hop 192.168.217.1

set security zones security-zone INTERNET host-inbound-traffic system-services ping
set security zones security-zone INTERNET host-inbound-traffic system-services rpm
set security zones security-zone INTERNET interfaces ge-0/0/0.915
set security zones security-zone INTERNET interfaces ge-0/0/0.917

set interfaces ge-0/0/0 vlan-tagging
set interfaces ge-0/0/0 unit 915 description Probe-Server
set interfaces ge-0/0/0 unit 915 vlan-id 915
set interfaces ge-0/0/0 unit 915 family inet address 187.188.12.50/28
set interfaces ge-0/0/0 unit 917 description Probe-Server1
set interfaces ge-0/0/0 unit 917 vlan-id 917
set interfaces ge-0/0/0 unit 917 family inet address 192.168.217.2/28

set routing-options static route 0.0.0.0/0 next-table FBF-1.inet.0
set routing-options interface-routes rib-group inet IMPORT-PHY
set routing-options rib-groups IMPORT-PHY import-rib inet.0
set routing-options rib-groups IMPORT-PHY import-rib FBF-1.inet.0
set routing-options rib-groups IMPORT-PHY import-rib FBF-2.inet.0

set firewall filter F1 term 2 then routing-instance FBF-1
set routing-instances FBF-1 instance-type forwarding
set routing-instances FBF-1 routing-options static route 0.0.0.0/0 next-hop 187.188.12.49

set firewall filter F1 term 1 then routing-instance FBF-2
set routing-instances FBF-2 instance-type forwarding
set routing-instances FBF-2 routing-options static route 0.0.0.0/0 next-hop 192.168.217.1`);
  }
  if (v.CLEAR_ALERTS) {
    parts.push(`# ============== CLEAR_ALERTS ==============

request system autorecovery state save
request system configuration rescue save
set chassis alarm management-ethernet link-down ignore`);
  }
  return parts.join("\n\n");
}

// ─── Main entry point ─────────────────────────────────────────────────────────

/** Render only the vendor-VLAN section (mirrors sections/vendors.j2). */
export function renderVendorVlansConfig(v: FirewallConfigVars): string {
  return renderVendors(v);
}

/** Render only the routable-VLAN sections (interfaces, zones, DHCP, address book, NAT, security policies). */
export function renderRoutableVlansConfig(v: FirewallConfigVars): string {
  const sections = [
    ["# ============================================", "# INTERFACES", "# ============================================", renderInterfaces(v)],
    ["# ============================================", "# ZONES", "# ============================================", renderZones(v)],
    ["# ============================================", "# DHCP", "# ============================================", renderDhcp(v)],
    ["# ============================================", "# ADDRESS BOOK", "# ============================================", renderAddressBook(v)],
    ["# ============================================", "# NAT", "# ============================================", renderNat(v)],
    ["# ============================================", "# SECURITY POLICIES", "# ============================================", renderSecurityPolicies(v)],
  ];
  return sections
    .flatMap((s) => { const v = s.join("\n"); return v ? [v] : []; })
    .join("\n\n");
}

/** Render the complete Juniper firewall config from all sections (mirrors main.j2). */
export function renderFirewallConfig(v: FirewallConfigVars): string {
  const sections = [
    ["# ============================================", "# INITIAL", "# ============================================", renderInitial(v)],
    [renderInterfaces(v)],
    ["# ============================================", "# ZONES", "# ============================================", renderZones(v)],
    ["# ============================================", "# DHCP", "# ============================================", renderDhcp(v)],
    ["# ============================================", "# ADDRESS BOOK", "# ============================================", renderAddressBook(v)],
    ["# ============================================", "# NAT", "# ============================================", renderNat(v)],
    ["# ============================================", "# SECURITY POLICIES", "# ============================================", renderSecurityPolicies(v)],
    [renderSnmpAndProtocols(v)],
    [renderRoutes(v)],
    ["# ============================================", "# VENDORS", "# ============================================", renderVendors(v)],
    ["# ============================================", "# ADDITIONAL OPTIONS", "# ============================================", renderAdditionalOptions(v)],
  ];

  return sections
    .flatMap((s) => { const v = s.join("\n"); return v ? [v] : []; })
    .join("\n\n");
}
